import type { ZodType } from 'zod';

// Access token lives in memory only (set by the auth provider). The refresh token
// is an httpOnly cookie the browser sends automatically with `credentials: 'include'`.
let accessToken: string | null = null;
let refreshHandler: (() => Promise<boolean>) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

// The auth provider registers how to refresh; the interceptor calls it on a 401.
export function registerRefreshHandler(handler: () => Promise<boolean>): void {
  refreshHandler = handler;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request(path: string, init: RequestInit, allowRetry = true): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (accessToken) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(path, { ...init, headers, credentials: 'include' });

  // Transparently refresh once on an expired access token, then retry the call.
  if (response.status === 401 && allowRetry && refreshHandler) {
    const refreshed = await refreshHandler();
    if (refreshed) {
      return request(path, init, false);
    }
  }

  return response;
}

async function toError(response: Response, method: string, path: string): Promise<ApiError> {
  let message = `${method} ${path} failed: ${response.status}`;
  let body: unknown = null;
  try {
    body = await response.json();
    if (typeof body === 'object' && body !== null && 'message' in body) {
      const raw = (body as { message: unknown }).message;
      message = Array.isArray(raw) ? raw.join(', ') : String(raw);
    }
  } catch {
    // Non-JSON error body — keep the status-line message.
  }
  return new ApiError(response.status, message, body);
}

export async function apiGet<T>(path: string, schema: ZodType<T>): Promise<T> {
  const response = await request(path, { method: 'GET' });
  if (!response.ok) {
    throw await toError(response, 'GET', path);
  }
  return schema.parse(await response.json());
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const response = await request(path, { method: 'GET' });
  if (!response.ok) {
    throw await toError(response, 'GET', path);
  }
  return response.blob();
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT',
  path: string,
  body: unknown,
  schema: ZodType<T>,
): Promise<T> {
  const response = await request(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) {
    throw await toError(response, method, path);
  }
  return schema.parse(await response.json());
}

export async function apiSendForm<T>(path: string, form: FormData, schema: ZodType<T>): Promise<T> {
  const response = await request(path, { method: 'POST', body: form });
  if (!response.ok) {
    throw await toError(response, 'POST', path);
  }
  return schema.parse(await response.json());
}

export async function apiDelete(path: string): Promise<void> {
  const response = await request(path, { method: 'DELETE' });
  if (!response.ok) {
    throw await toError(response, 'DELETE', path);
  }
}
